import { memo } from 'react';
import { PURCHASES, PurchaseKind, PurchaseState, getPurchaseState, purchaseCost, type PurchaseDefinition } from '../../game/data/purchases';
import type { GameRuntime } from '../../game/runtime/GameRuntime';
import type { RuntimeUISnapshot } from '../../game/runtime/RuntimeUIStore';
import { CORE_BEAMS, DEVICES, NODE_LENSES } from '../../game/weapons/definitions';

export function purchaseGlyph(item: PurchaseDefinition): string {
  if (item.kind === PurchaseKind.Core) return CORE_BEAMS[item.target]?.glyph ?? '▸';
  if (item.kind === PurchaseKind.Lens) return NODE_LENSES[item.target]?.glyph ?? '→';
  if (item.kind === PurchaseKind.Device) return DEVICES[item.target]?.glyph ?? '+';
  if (item.kind === PurchaseKind.MaxLife) return '♥';
  if (item.kind === PurchaseKind.NodeRecharge) return '◉';
  if (item.kind === PurchaseKind.ScoreBoost) return '×2';
  return '✦';
}

interface GatePanelProps {
  runtime: GameRuntime;
  snapshot: RuntimeUISnapshot;
}

/** Fix 3(b): skips re-render when App re-renders for a reason unrelated to
 *  this snapshot/runtime (e.g. settingsOpen toggling). */
export const GatePanel = memo(function GatePanel({ runtime, snapshot }: GatePanelProps) {
  const selectedPurchase = PURCHASES[snapshot.gateSelection] ?? PURCHASES[0]!;
  const selectedCount = runtime.world.player.purchaseCounts[selectedPurchase.id] ?? 0;
  const selectedPurchaseState = getPurchaseState(runtime.world.player, selectedPurchase, runtime.world.difficulty);
  const selectedPrice = purchaseCost(runtime.world.player, selectedPurchase, runtime.world.difficulty);
  const canBuySelected = selectedPurchaseState === PurchaseState.Buy || selectedPurchaseState === PurchaseState.Equip;
  const selectedActionLabel = selectedPurchaseState === PurchaseState.Equip
    ? 'EQUIP'
    : selectedPurchaseState === PurchaseState.Equipped
      ? 'EQUIPPED'
      : selectedPurchaseState === PurchaseState.Maxed
        ? 'MAXED'
        : selectedPurchaseState === PurchaseState.RepairNotNeeded
          ? 'FULL'
          : selectedPurchaseState === PurchaseState.InsufficientLux
            ? `NEED ${selectedPrice}`
            : `BUY ${selectedPrice}`;

  return (
    <section className="gate-accessibility" aria-labelledby="gate-access-title">
      <div>
        <p className="eyebrow">ACCESSIBLE CALIBRATION MIRROR</p>
        <h2 id="gate-access-title">{purchaseGlyph(selectedPurchase)} {selectedPurchase.name}</h2>
        <p>{selectedCount}/{selectedPurchase.maxPurchases} acquired · {selectedPrice === 0 ? 'owned equipment' : `${selectedPrice} LUX`}</p>
        <p className="sr-only" aria-live="polite">{snapshot.gateMessage || `${selectedPurchase.name} selected`}</p>
      </div>
      <div className="gate-actions">
        <button data-menu-item type="button" onClick={() => runtime.selectPurchase((snapshot.gateSelection + PURCHASES.length - 1) % PURCHASES.length)}>PREVIOUS</button>
        <button data-menu-item type="button" className="primary" disabled={!canBuySelected} onClick={() => runtime.purchase()}>{selectedActionLabel}</button>
        <button data-menu-item type="button" onClick={() => runtime.selectPurchase((snapshot.gateSelection + 1) % PURCHASES.length)}>NEXT</button>
        <button data-menu-item type="button" onClick={() => runtime.leaveGate()}>LEAVE GATE</button>
      </div>
    </section>
  );
});
